import { useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { MdKeyboardCommandKey } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@chakra-ui/icons';
import {
  Button,
  Card,
  CardBody,
  Flex,
  Grid,
  GridItem,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { ProviderCardNew } from '../../../components';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import { updateAppDetails } from '../../../redux/appBuilder/appDetails';
import { getAppDetails } from '../../../redux/appBuilder/appDetails/selectors';
import { Application } from '../../../redux/appBuilder/appDetails/types';
import {
  updateAppActiveStep,
  updateSelectedProviders,
} from '../../../redux/appBuilder/stepperDetails';
import {
  getAppActiveStep,
  getAppStepperDetails,
  getSelectedProviders,
} from '../../../redux/appBuilder/stepperDetails/selectors';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { HttpsProvider } from '../../../redux/httpsProviders/types';
import { resetMobileClient } from '../../../redux/providerBuilder/mobileClient';
import { resetNetworkStore } from '../../../redux/providerBuilder/networkRequestStore';
import { resetProviderDetails } from '../../../redux/providerBuilder/providerDetails';
import { resetSelectedInstances } from '../../../redux/providerBuilder/selectedInstances';
import { resetSelectedNetworkRequest } from '../../../redux/providerBuilder/selectedNetworkRequest';
import { resetActiveStep } from '../../../redux/providerBuilder/stepperDetails';
import { getAllExploreProvidersWithPagination } from '../../../utils/app-builder/fetch-providers';
import {
  editApplication,
  getImageUrl,
  registerNewApplication,
} from '../../../utils/app-builder/register-application';
import { generateEncryptionKeys } from '../../../utils/generate-wallet-address';
import { useDebounce } from '../../../utils/logs/send-logs';

export const SelectProvider = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const activeStep = useAppSelector(getAppActiveStep);
  const appData = useAppSelector(getAppDetails);
  const appStepperDetails = useAppSelector(getAppStepperDetails);

  const [searchQuery, setSearchQuery] = useState('');
  const [providerOptions, setProviderOptions] = useState<any>([]);
  const [selectedProviders, setSelectedProviders] = useState<any>(
    useAppSelector(getSelectedProviders)
  );
  const debouncedSearchTerm = useDebounce(searchQuery, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const { width, height } = useWindowDimensions();

  const getPageSizeForScreen = () => {
    if (width < 1360 && width >= 1000) {
      return 4;
    } else if (width < 1650 && width >= 1360) {
      return 6;
    } else if (width < 2000 && width >= 1650) {
      return 8;
    } else if (width < 2420 && width >= 2000) {
      return 12;
    } else {
      return 16;
    }
  };

  const pageSize = getPageSizeForScreen();

  const fetchProviderData = async (pageKey: number) => {
    const exploreProvidersDetails = await getAllExploreProvidersWithPagination(
      pageKey * pageSize,
      pageSize,
      searchQuery,
      'ALL'
    );
    setProviderOptions([...exploreProvidersDetails?.providers]);
    setTotalPages(Math.ceil(exploreProvidersDetails?.count / pageSize));
  };

  useEffect(() => {
    fetchProviderData(currentPage - 1);
  }, [debouncedSearchTerm, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          size="sm"
          className="x-small-font"
          onClick={() => handlePageChange(i)}
          bgColor={currentPage === i ? 'whitesmoke' : 'white'}
        >
          {i}
        </Button>
      );
    }

    return pages;
  };

  const removeSelectedProviders = (httpProviderId: string) => {
    const updatedProviders = selectedProviders.filter(
      (p: any) => p.httpProviderId !== httpProviderId
    );
    setSelectedProviders(updatedProviders);
  };

  const createNewProvider = () => {
    dispatch(updateSelectedProviders(selectedProviders));
    dispatch(resetNetworkStore());
    dispatch(resetProviderDetails());
    dispatch(resetSelectedInstances());
    dispatch(resetSelectedNetworkRequest());
    dispatch(resetMobileClient());
    dispatch(resetActiveStep());
    navigate('/provider-builder');
  };

  const handleProviderSelect = (selectedProviderId: string) => {
    // get the selected provider from the list
    const selectedProvider = providerOptions.find(
      (p: any) => p.httpProviderId === selectedProviderId
    );
    // Check if provider is already selected
    const isProviderSelected = selectedProviders.some(
      (p: any) => p.httpProviderId === selectedProvider.httpProviderId
    );
    if (!isProviderSelected) {
      setSelectedProviders([...selectedProviders, selectedProvider]);
    } else {
      removeSelectedProviders(selectedProvider.httpProviderId);
    }
  };

  const uploadImage = async (appData: Application, address: string) => {
    let imageUrl = '';
    if (appData.appImageExists) {
      const data = await getImageUrl(appData.appImageUrl || '', address);
      if (data?.error) {
        toast({
          title: 'Error',
          description: 'Failed to upload image',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      }

      imageUrl = data?.url || '';
    }

    return imageUrl;
  };

  const handleApplicationRegistration = async (appData: Application) => {
    const { address, privateKey } = await generateEncryptionKeys();
    const selectedProviderIds = selectedProviders.map(
      (p: HttpsProvider) => p.httpProviderId
    );
    const imageUrl = await uploadImage(appData, address);

    const newAppData = {
      ...appData,
      httpProviderIds: selectedProviderIds,
      publicKey: address,
    };
    newAppData.appImageUrl = imageUrl.split('?')[0];
    const responseData = await registerNewApplication(newAppData);
    if (responseData?.isSuccess) {
      dispatch(
        updateAppDetails({
          appId: address,
          privateKey: privateKey,
          httpProvider: selectedProviders,
          appImageUrl: imageUrl.split('?')[0],
        })
      );
      dispatch(updateAppActiveStep(activeStep + 1));
      dispatch(updateSelectedProviders(selectedProviders));
      setIsLoading(false);
      navigate(`/my-applications/code-preview/${address}`);
    } else {
      toast({
        title: 'Error',
        description: 'Failed to register application',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const handleEditApplicationRegistration = async (appData: Application) => {
    const selectedProviderIds = selectedProviders.map(
      (p: HttpsProvider) => p.httpProviderId
    );

    let originalImageUrl = appData.appImageUrl;
    if (appStepperDetails.updatedImage) {
      const imageUrl = await uploadImage(appData, appData.appId);
      originalImageUrl = imageUrl.split('?')[0];
    }

    const newAppData = { ...appData, httpProviderIds: selectedProviderIds };
    newAppData.appImageUrl = originalImageUrl;
    const data = await editApplication(newAppData);
    if (data?.isSuccess) {
      toast({
        title: 'Application updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });

      dispatch(updateSelectedProviders(selectedProviders));
      dispatch(updateAppActiveStep(activeStep + 1));
      dispatch(
        updateAppDetails({
          httpProvider: selectedProviders,
          appImageUrl: originalImageUrl,
        })
      );
      navigate(`/edit-application/code-preview/${appData.appId}`);
    } else {
      toast({
        title: 'Error',
        description: 'Failed to update application',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const handleNext = () => {
    if (selectedProviders.length === 0) {
      // toast message to select at least one provider
      toast({
        title: 'Select at least one provider',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } else {
      setIsLoading(true);
      if (appStepperDetails.editAction) {
        const newAppData: Application = {
          name: appData.name,
          appId: appData.appId,
          description: appData.description,
          projectName: appData.projectName,
          appType: appData.appType as string,
          appPlatform: appData.appPlatform,
          appImageUrl: appData.appImageUrl ? appData.appImageUrl : appData.appImageUrl,
          appImageExists: !!appData.appImageUrl,
        };

        handleEditApplicationRegistration(newAppData);
      } else {
        handleApplicationRegistration(appData);
      }
    }
  };

  const handleBack = () => {
    dispatch(updateAppActiveStep(activeStep - 1));
  };

  return (
    <Card
      w={width > 1740 ? '1300px' : '95%'}
      mt={6}
      variant="outline"
      borderRadius="12px"
      h="fit-content"
    >
      <CardBody p={0}>
        <VStack
          align="flex-start"
          spacing={5}
          padding={7}
          pb={0}
          h="72vh"
          overflowY="scroll"
        >
          <VStack align="flex-start" spacing="5px" w="100%">
            <Text
              fontFamily="inter"
              className="medium-font"
              fontWeight="bold"
              color="black"
            >
              Select Providers for your App
            </Text>
            <Text
              color="gray.500"
              className="small-font"
              whiteSpace="pre-line"
              fontFamily="inter"
            >
              Providers are pre built configurations to extract data from the website
            </Text>
            <HStack spacing="20px" mt={3} w="100%">
              <Flex alignItems="center" justifyContent="flex-start" gap={5} w="80%">
                <InputGroup w="240px">
                  <InputLeftElement pointerEvents="none">
                    <FiSearch color="gray" />
                  </InputLeftElement>
                  <Input
                    size="sm"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    type="search"
                    display="flex"
                    alignContent="center"
                    placeholder="Search"
                    className="small-font"
                    borderRadius="8px"
                    variant="filled"
                    bgColor="#f3f5fc"
                    py={5}
                    fontWeight={400}
                  />
                </InputGroup>
              </Flex>
              <Button
                leftIcon={<MdKeyboardCommandKey color="black" />}
                justifyContent="center"
                alignItems="center"
                className="small-font"
                borderRadius="8px"
                borderWidth="1px"
                onClick={createNewProvider}
                boxShadow={1}
                variant="outline"
                size="xs"
                py={5}
                fontWeight={500}
                w="20%"
              >
                Create New Provider
              </Button>
            </HStack>
          </VStack>
          {selectedProviders?.length && (
            <VStack>
              <HStack wrap="wrap">
                {selectedProviders.map((p: any, i: number) => (
                  <Tag size="md" variant="solid" bgColor="gray.700" color="white" key={i}>
                    <TagLabel maxW="150px" className="small-font">
                      {p?.name}
                    </TagLabel>
                    <TagCloseButton
                      onClick={() => removeSelectedProviders(p.httpProviderId)}
                    />
                  </Tag>
                ))}
              </HStack>
            </VStack>
          )}

          <Grid
            w="100%"
            mt={selectedProviders?.length ? '0px' : '32px'}
            templateColumns="repeat(auto-fill,minmax(255px,1fr))"
            gap={height < 1000 ? '12px' : '24px'}
          >
            {providerOptions.map((provider: any) => (
              <GridItem key={provider.httpProviderId}>
                <ProviderCardNew
                  providerDetail={provider}
                  pointerCursor={true}
                  showEditButton={false}
                  onSelect={handleProviderSelect}
                  isSelected={selectedProviders.some(
                    (p: any) => p.httpProviderId === provider.httpProviderId
                  )}
                  removeActiveTag={true}
                />
              </GridItem>
            ))}
          </Grid>
          <HStack
            w="100%"
            padding="8px"
            justifyContent="space-between"
            alignItems="center"
          >
            <Text fontFamily="inter" className="x-small-font">
              Page {currentPage} of {totalPages}
            </Text>
            <HStack spacing={2}>
              <Button
                size="sm"
                bgColor="whitesmoke"
                className="x-small-font"
                onClick={() => handlePageChange(1)}
                isDisabled={currentPage === 1}
              >
                <ArrowLeftIcon boxSize={3} />
              </Button>
              <Button
                size="sm"
                bgColor="whitesmoke"
                className="x-small-font"
                onClick={() => handlePageChange(currentPage - 1)}
                isDisabled={currentPage === 1}
              >
                <ChevronLeftIcon boxSize={4} />
              </Button>
              {renderPageNumbers()}
              <Button
                size="sm"
                bgColor="whitesmoke"
                className="x-small-font"
                onClick={() => handlePageChange(currentPage + 1)}
                isDisabled={currentPage === totalPages}
              >
                <ChevronRightIcon boxSize={4} />
              </Button>
              <Button
                size="sm"
                bgColor="whitesmoke"
                className="x-small-font"
                onClick={() => handlePageChange(totalPages)}
                isDisabled={currentPage === totalPages}
              >
                <ArrowRightIcon boxSize={3} />
              </Button>
            </HStack>
          </HStack>
        </VStack>
        <VStack className="stepper-bar" alignItems="space-between">
          <Flex alignItems="center" gap={3} justifyContent="space-between">
            <Button
              onClick={handleBack}
              isDisabled={!!isLoading}
              className="stepper-back-button small-font"
              _hover={{
                transform: 'scale(1.05)',
              }}
            >
              Back
            </Button>
            <Flex alignItems="center" gap="17px">
              <Text color="gray.500" className="x-small-font">
                2/4
              </Text>
              <Button
                onClick={handleNext}
                isLoading={isLoading}
                className="small-font stepper-next-button"
                fontFamily="inter"
                _hover={{
                  transform: 'scale(1.05)',
                }}
              >
                {appStepperDetails.editAction ? 'Edit' : 'Next'}
              </Button>
            </Flex>
          </Flex>
        </VStack>
      </CardBody>
    </Card>
  );
};
